import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { CircularProgress } from "@mui/material";
import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApi } from "../../contexts/api";
import { useAuth } from "../../contexts/auth";
import { useChatbotService } from "../../services/chatbotService";
import iconeBotao from "./assets/waybo-botao.svg";
import iconeCabecalho from "./assets/waybo-header.svg";
import iconeIniciar from "./assets/waybo-iniciar.svg";
import { chatbotConversaReducer, estadoInicialChatbot,ETAPA } from "./chatbotConversaReducer";
import { contemHtml, sanitizarHtml } from "./conteudoHtml";
import { lerConversa, limparConversa, salvarConversa } from "./conversaStorage";
import * as S from "./styles";

const NOME_ASSISTENTE = "WayBot";
const NOTAS = [0, 1, 2, 3, 4, 5];
const URL_WHATSAPP_SUPORTE = "https://api.whatsapp.com/send?l=pt_br&phone=551731228600";

const AvisoSuporte = () => {
  const { t: tChatbot } = useTranslation("chatbotSuporte");

  return (
    <S.Aviso>
      {tChatbot("messages.notice")}{" "}
      <S.LinkSuporte href={URL_WHATSAPP_SUPORTE} target="_blank" rel="noopener noreferrer">
        {tChatbot("links.whatsapp")}
      </S.LinkSuporte>
    </S.Aviso>
  );
};

export const ChatbotSuporteButton = ({ onClick }) => {
  const { t: tChatbot } = useTranslation("chatbotSuporte");

  return (
    <S.BotaoFlutuante
      type="button"
      aria-label={tChatbot("buttons.open")}
      title={tChatbot("labels.support")}
      onClick={onClick}
    >
      <img src={iconeBotao} alt="" />
    </S.BotaoFlutuante>
  );
};

const AvatarAssistente = ({ tamanho, destaque }) => (
  <S.Avatar $tamanho={tamanho} $destaque={destaque}>
    <img src={iconeCabecalho} alt="" />
  </S.Avatar>
);

const MensagemAssistente = ({ children }) => (
  <S.LinhaMensagem>
    <AvatarAssistente tamanho={24} />
    <S.Balao>{children}</S.Balao>
  </S.LinhaMensagem>
);

// A EMBED pode responder em HTML; o conteúdo é sanitizado antes de ir ao DOM.
const ConteudoAssistente = ({ conteudo }) =>
  contemHtml(conteudo) ? (
    <S.LinhaMensagem>
      <AvatarAssistente tamanho={24} />
      <S.Balao $html dangerouslySetInnerHTML={{ __html: sanitizarHtml(conteudo) }} />
    </S.LinhaMensagem>
  ) : (
    <MensagemAssistente>{conteudo}</MensagemAssistente>
  );

const MensagemUsuario = ({ children }) => (
  <S.LinhaMensagem $usuario>
    <S.Balao $usuario>{children}</S.Balao>
  </S.LinhaMensagem>
);

const PesquisaSatisfacao = ({ pesquisa, onResolvido, onNota }) => {
  const { t } = useTranslation();
  const { t: tChatbot } = useTranslation("chatbotSuporte");
  const { etapa, resolvido, nota, comentario, enviando } = pesquisa;
  const respondeuResolvido = etapa !== ETAPA.RESOLVIDO;
  const respondeuNota = nota !== null;

  return (
    <S.BlocoPesquisa>
      <MensagemAssistente>{tChatbot("messages.resolvedQuestion")}</MensagemAssistente>
      <S.OpcoesResolvido role="group" aria-label={tChatbot("messages.resolvedQuestion")}>
        <S.BotaoOpcao
          type="button"
          aria-pressed={resolvido === true}
          $selecionado={resolvido === true}
          disabled={respondeuResolvido}
          onClick={() => onResolvido(true)}
        >
          <ThumbUpOutlinedIcon /> {t("status.yes")}
        </S.BotaoOpcao>
        <S.BotaoOpcao
          type="button"
          aria-pressed={resolvido === false}
          $selecionado={resolvido === false}
          disabled={respondeuResolvido}
          onClick={() => onResolvido(false)}
        >
          <ThumbDownOutlinedIcon /> {t("status.no")}
        </S.BotaoOpcao>
      </S.OpcoesResolvido>

      {respondeuResolvido && (
        <>
          <MensagemAssistente>{tChatbot("messages.ratingQuestion")}</MensagemAssistente>
          <S.Notas role="group" aria-label={tChatbot("labels.rating")}>
            {NOTAS.map((valor) => (
              <S.BotaoNota
                key={valor}
                type="button"
                aria-label={tChatbot("labels.ratingValue", { nota: valor })}
                aria-pressed={nota === valor}
                $selecionado={nota === valor}
                disabled={respondeuNota || enviando}
                onClick={() => onNota(valor)}
              >
                {valor}
              </S.BotaoNota>
            ))}
          </S.Notas>
        </>
      )}

      {etapa === ETAPA.COMENTARIO && <MensagemAssistente>{tChatbot("messages.commentRequest")}</MensagemAssistente>}
      {etapa === ETAPA.CONCLUIDA && comentario === null && (
        <MensagemAssistente>{tChatbot("messages.thanks")}</MensagemAssistente>
      )}
      {etapa === ETAPA.CONCLUIDA && comentario !== null && (
        <>
          <MensagemAssistente>{tChatbot("messages.commentRequest")}</MensagemAssistente>
          <MensagemUsuario>{comentario}</MensagemUsuario>
          <MensagemAssistente>{tChatbot("messages.commentThanks")}</MensagemAssistente>
        </>
      )}
    </S.BlocoPesquisa>
  );
};

// A conversa e o texto do campo vêm do ChatbotSuporte, que continua montado com o painel fechado.
// geracaoRef muda na troca de empresa: respostas pendentes de antes da troca são descartadas.
export const ChatbotSuportePanel = ({ estado, dispatch, texto, setTexto, geracaoRef, onClose }) => {
  const { t } = useTranslation();
  const { t: tChatbot } = useTranslation("chatbotSuporte");
  const listaRef = useRef(null);
  const campoRef = useRef(null);
  const enviandoRef = useRef(false);

  // useChatbotService() devolve um objeto novo a cada render: nunca vai em deps.
  const servico = useChatbotService();
  const servicoRef = useRef(servico);
  servicoRef.current = servico;

  const { iniciada, messages, loading, error, conversationId, messageIds, pesquisa } = estado;
  const { etapa } = pesquisa;
  const aguardandoComentario = etapa === ETAPA.COMENTARIO;
  const campoHabilitado =
    iniciada && !loading && !pesquisa.enviando && (etapa === null || etapa === ETAPA.RESOLVIDO || aguardandoComentario);
  const textoValido = texto.trim().length > 0;
  const exibirFinalizar = etapa === ETAPA.NOTA || aguardandoComentario || etapa === ETAPA.CONCLUIDA;
  const podeFinalizar = pesquisa.nota !== null && !pesquisa.enviando;

  useEffect(() => {
    if (listaRef.current) {
      listaRef.current.scrollTop = listaRef.current.scrollHeight;
    }
  }, [messages, loading, error, pesquisa]);

  useEffect(() => {
    if (campoHabilitado) {
      campoRef.current?.focus();
    }
  }, [campoHabilitado]);

  // O campo cresce com o texto; o max-height do styles.js o limita à metade do painel.
  useLayoutEffect(() => {
    const campo = campoRef.current;
    if (campo) {
      campo.style.height = "auto";
      campo.style.height = `${campo.scrollHeight}px`;
    }
  }, [texto, iniciada]);

  const enviar = async () => {
    const conteudo = texto.trim();
    if (!conteudo || !campoHabilitado || enviandoRef.current) {
      return;
    }

    // Com a pesquisa pedindo comentário, o texto vira o comentário da avaliação e não vai à EMBED.
    if (aguardandoComentario) {
      dispatch({ type: "COMENTAR", comentario: conteudo });
      setTexto("");
      return;
    }

    enviandoRef.current = true;
    const geracao = geracaoRef.current;
    dispatch({ type: "ENVIAR", content: conteudo });
    setTexto("");

    try {
      const resposta = await servicoRef.current.enviarPergunta({
        question: conteudo,
        conversationId,
        messageIds,
      });
      if (geracao !== geracaoRef.current) {
        return;
      }
      dispatch({ type: "SUCESSO", resposta });
    } catch (erro) {
      if (geracao !== geracaoRef.current) {
        return;
      }
      dispatch({ type: "FALHA", error: "errors.answer" });
      setTexto(conteudo);
    } finally {
      enviandoRef.current = false;
    }
  };

  const aoPressionarTecla = (evento) => {
    if (evento.key === "Enter" && !evento.shiftKey) {
      evento.preventDefault();
      enviar();
    }
  };

  const finalizar = async () => {
    if (!podeFinalizar) {
      return;
    }
    // Comentário digitado e não enviado também é aproveitado.
    const comentarioDigitado = aguardandoComentario && texto.trim() ? texto.trim() : null;
    const geracao = geracaoRef.current;
    dispatch({ type: "FINALIZAR_INICIO" });
    try {
      await servicoRef.current.avaliarConversa({
        conversationId,
        resolvido: pesquisa.resolvido,
        nota: pesquisa.nota,
        comentario: pesquisa.comentario ?? comentarioDigitado,
      });
    } catch (erro) {
      if (geracao !== geracaoRef.current) {
        return;
      }
      // 409: a conversa já foi avaliada; não há o que reenviar.
      if (erro?.response?.status !== 409) {
        dispatch({ type: "FINALIZAR_FALHA", error: "errors.rating" });
        return;
      }
    }
    if (geracao !== geracaoRef.current) {
      return;
    }
    dispatch({ type: "NOVA_CONVERSA" });
    setTexto("");
  };

  return (
    <S.Painel role="dialog" aria-label={tChatbot("labels.dialog", { nome: NOME_ASSISTENTE })}>
      <S.Cabecalho>
        <AvatarAssistente tamanho={32} destaque />
        <S.NomeAssistente>{NOME_ASSISTENTE}</S.NomeAssistente>
        <S.BotaoIcone type="button" aria-label={t("buttons.close")} onClick={onClose}>
          <CloseIcon />
        </S.BotaoIcone>
      </S.Cabecalho>

      {!iniciada ? (
        <>
          <S.EstadoVazio>
            <S.Apresentacao>
              <strong>{tChatbot("messages.introTitle", { nome: NOME_ASSISTENTE })}</strong>
              {tChatbot("messages.introDescription")}
            </S.Apresentacao>
            <AvisoSuporte />
          </S.EstadoVazio>
          <S.Rodape>
            <S.BotaoIniciar type="button" onClick={() => dispatch({ type: "INICIAR" })}>
              {tChatbot("buttons.start")}
              <img src={iconeIniciar} alt="" />
            </S.BotaoIniciar>
          </S.Rodape>
        </>
      ) : (
        <>
          <S.Mensagens ref={listaRef} aria-live="polite">
            <AvisoSuporte />
            {messages.map((mensagem) =>
              mensagem.role === "user" ? (
                <MensagemUsuario key={mensagem.id}>{mensagem.content}</MensagemUsuario>
              ) : mensagem.chave ? (
                <MensagemAssistente key={mensagem.id}>{tChatbot(mensagem.chave)}</MensagemAssistente>
              ) : (
                <ConteudoAssistente key={mensagem.id} conteudo={mensagem.content} />
              ),
            )}
            {loading && (
              <S.Digitando role="status">
                <CircularProgress size={16} color="inherit" />
                {tChatbot("labels.typing", { nome: NOME_ASSISTENTE })}
              </S.Digitando>
            )}
            {etapa !== null && (
              <PesquisaSatisfacao
                pesquisa={pesquisa}
                onResolvido={(resolvido) => dispatch({ type: "RESPONDER_RESOLVIDO", resolvido })}
                onNota={(nota) => dispatch({ type: "RESPONDER_NOTA", nota })}
              />
            )}
            {error && !loading && <S.MensagemErro role="alert">{tChatbot(error)}</S.MensagemErro>}
          </S.Mensagens>

          <S.Rodape>
            <S.CampoMensagem>
              <S.AreaTexto
                ref={campoRef}
                rows={1}
                maxLength={2000}
                placeholder={tChatbot("placeholders.message")}
                aria-label={tChatbot("labels.message")}
                value={texto}
                disabled={!campoHabilitado}
                onChange={(evento) => setTexto(evento.target.value)}
                onKeyDown={aoPressionarTecla}
              />
              <S.BotaoEnviar
                type="button"
                aria-label={tChatbot("buttons.send")}
                disabled={!campoHabilitado || !textoValido}
                onClick={enviar}
              >
                <SendIcon />
              </S.BotaoEnviar>
            </S.CampoMensagem>
            {exibirFinalizar && (
              <S.BotaoFinalizar type="button" disabled={!podeFinalizar} onClick={finalizar}>
                {pesquisa.enviando ? <CircularProgress size={16} color="inherit" /> : tChatbot("buttons.finish")}
              </S.BotaoFinalizar>
            )}
          </S.Rodape>
        </>
      )}
    </S.Painel>
  );
};

const ChatbotSuporte = () => {
  const { dadosAuth, loading: carregandoAuth, logado } = useAuth();
  const { token } = useApi();
  const [disponivel, setDisponivel] = useState(false);
  const [aberto, setAberto] = useState(false);
  // Fechar o painel ou recarregar a página não reinicia a conversa: só "Finalizar", a troca de empresa ou o logoff.
  const [conversa, dispatch] = useReducer(chatbotConversaReducer, estadoInicialChatbot);
  const [texto, setTexto] = useState("");
  const geracaoRef = useRef(0);

  // useChatbotService() devolve um objeto novo a cada render: nunca vai em deps.
  const servicoRef = useRef(null);
  servicoRef.current = useChatbotService();

  const idEmpresa = dadosAuth?.empresaDTO?.id;
  const idUsuario = dadosAuth?.usuarioLogadoDTO?.id;
  const contextoPronto = !carregandoAuth && Boolean(dadosAuth) && Boolean(token);
  const deslogado = !carregandoAuth && logado === false;

  // O token do ApiProvider só é atualizado depois que a sessão da nova empresa
  // é gravada, então depender dele evita consultar com os dados da empresa anterior.
  useEffect(() => {
    setDisponivel(false);
    setAberto(false);
    // A conversa pertence à empresa: o conversationId de outra empresa seria recusado (403).
    geracaoRef.current += 1;
    dispatch({ type: "NOVA_CONVERSA" });
    setTexto("");
    if (deslogado) {
      limparConversa();
    }
    if (!contextoPronto) {
      return undefined;
    }

    // Lida antes do efeito de gravação abaixo, que roda em seguida com o estado ainda vazio.
    const salva = lerConversa(idEmpresa, idUsuario);
    if (salva) {
      dispatch({ type: "RESTAURAR", conversa: salva.conversa });
      setTexto(salva.texto);
    }

    let cancelado = false;
    servicoRef.current.buscarDisponibilidade().then((resultado) => {
      if (!cancelado) {
        setDisponivel(resultado === true);
      }
    });

    return () => {
      cancelado = true;
    };
  }, [contextoPronto, deslogado, idEmpresa, idUsuario, token]);

  useEffect(() => {
    if (!contextoPronto) {
      return;
    }
    if (conversa.iniciada) {
      salvarConversa({ idEmpresa, idUsuario, conversa, texto });
    } else {
      limparConversa();
    }
  }, [conversa, texto]);

  if (!disponivel) {
    return null;
  }

  return (
    <>
      {aberto ? (
        <ChatbotSuportePanel
          estado={conversa}
          dispatch={dispatch}
          texto={texto}
          setTexto={setTexto}
          geracaoRef={geracaoRef}
          onClose={() => setAberto(false)}
        />
      ) : (
        <ChatbotSuporteButton onClick={() => setAberto(true)} />
      )}
    </>
  );
};

export default ChatbotSuporte;
